import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { t } from '@/i18n/i18n';
import styles from './SectionErrorBoundary.module.css';

interface Props {
  /** Имя раздела для отчёта и текста. */
  scope: string;
  children: ReactNode;
  onReset?: () => void;
}

interface State {
  error: Error | null;
}

/** Граница ошибок по разделам (NFR-4): раздел падает — приложение остаётся. */
export class SectionErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // js_error уходит через глобальный обработчик; здесь только консоль для отладки.
    if (import.meta.env.DEV) console.error(`[${this.props.scope}]`, error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className={styles.fallback} role="alert">
        <p className={styles.eyebrow}>{t('errors.sectionTitle')}</p>
        <h2 className={styles.title}>{t('errors.sectionFailed')}</h2>
        <p className={styles.text}>{t('errors.sectionHint')}</p>
        <div className={styles.actions}>
          <Button variant="primary" size="sm" onClick={this.reset}>
            {t('common.tryAgain')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => window.location.reload()}>
            {t('errors.reload')}
          </Button>
        </div>
      </div>
    );
  }
}
